import React from 'react';
import moment from 'moment';
import { useAtom } from 'jotai';
import { View, Text } from '../../lib/react-native-compat';
import { fieldReportAtom } from './state';
import { isBlankRow } from './sectionDefs';
import { timeCardTotalHours } from './TimeCard';
import { useJobPhases } from './WorkPhaseChips';
import { usePhaseMetrics, phaseStatus } from './usePhaseMetrics';
import useQtyHistory from './useQtyHistory';

// Quick Report review: today's productivity, measured three ways —
//
//   plan    — the phase's budget quantity over its phased Burn Tool hours,
//             i.e. the units/hr the job was bid to run
//   pace    — what this crew actually ran over the last 7 days (prior field
//             reports + HeavyJob FP postings over JDE labor hours)
//   today   — the hours and quantity on this report
//
// Everything reduces to hours: quantity installed × the plan's hours-per-unit
// is what today earned, and earned minus worked is the gain or fade. That's
// the number that shows up on the PSR two weeks later, so it shows up here
// while the day can still be explained.
//
// Display only — nothing on this card is an input and nothing is stored.
// Reports typed before the recap replaced the two manual total boxes read
// their values back out of the hidden 'simple' section.

const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
};

const fmt = (n) => (Math.round(n * 10) / 10).toLocaleString();

const rateFmt = (n) => (n >= 100 ? Math.round(n) : Math.round(n * 10) / 10).toLocaleString();

// Signed hours, gain first: '+1.2 h' / '−3.4 h'.
const signed = (n) => `${n >= 0 ? '+' : '−'}${fmt(Math.abs(n))} h`;

const GAIN = '#047857';
const FADE = '#B91C1C';
const FLAT = '#334155';

const Tile = ({ value, label, sub, color }) => (
    <View style={{
        flex: 1, minWidth: 104, borderRadius: 10, backgroundColor: '#F8FAFC',
        border: '1px solid #E2E8F0', paddingTop: 10, paddingBottom: 10,
        paddingLeft: 10, paddingRight: 10,
    }}>
        <Text style={{
            fontSize: 20, fontWeight: '700', lineHeight: '24px',
            color: value === '—' ? '#CBD5E1' : (color || '#0F172A'),
        }}>
            {value}
        </Text>
        <Text style={{ fontSize: 10, fontWeight: '700', color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 2 }}>
            {label}
        </Text>
        {sub ? <Text style={{ fontSize: 11, color: '#94A3B8', marginTop: 2 }}>{sub}</Text> : null}
    </View>
);

// One finding. tone drives the marker: '▼' losing, '▲' gaining, '·' neutral.
const Finding = ({ tone, text }) => {
    const t = tone === 'fade'
        ? { glyph: '▼', fg: FADE, bg: '#FEF2F2', border: '#FECACA' }
        : tone === 'gain'
            ? { glyph: '▲', fg: GAIN, bg: '#ECFDF5', border: '#A7F3D0' }
            : { glyph: '·', fg: '#475569', bg: '#F8FAFC', border: '#E2E8F0' };
    return (
        <View style={{ flexDirection: 'row', alignItems: 'flex-start', columnGap: 8, marginTop: 8 }}>
            <View style={{
                width: 18, height: 18, borderRadius: 5, backgroundColor: t.bg,
                border: `1px solid ${t.border}`, alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
                <Text style={{ fontSize: 10, fontWeight: '700', color: t.fg, lineHeight: '16px' }}>{t.glyph}</Text>
            </View>
            <Text style={{ flex: 1, fontSize: 13, color: '#334155', lineHeight: '18px' }}>{text}</Text>
        </View>
    );
};

const DayRecap = () => {
    const [report] = useAtom(fieldReportAtom);
    const qtyHist = useQtyHistory(report);
    const { jobPhases, intervals } = useJobPhases(qtyHist);
    const { actualHours, fpQty, phasedHours } = usePhaseMetrics();

    if (!report) return null;

    const totals = (report.sections?.simple || [])[0] || {};
    const reportDate = report.reportDate;
    const crewRows = (report.sections?.timeCard || []).filter((r) => !isBlankRow(r));
    const qtyRows = report.sections?.quantities || [];
    const workRows = report.sections?.work || [];

    // Phases picked on the Progress tab, in pick order.
    const codes = [];
    const seen = new Set();
    for (const r of workRows) {
        const code = String(r?.costCode || '').trim();
        if (!code || seen.has(code)) continue;
        seen.add(code);
        codes.push(code);
    }

    const tcHours = timeCardTotalHours(report.sections?.timeCard);
    const hours = tcHours > 0 ? tcHours : num(totals.totalHours);
    const perPerson = crewRows.map((r) => ({
        name: String(r?.name || '').trim(),
        hrs: Object.values(r?.hours || {}).reduce((t, v) => t + num(v), 0),
    }));
    const crewCount = perPerson.filter((p) => p.hrs > 0 || p.name).length;

    const qtyByUnit = {};
    qtyRows.forEach((r) => {
        const q = num(r?.installedToday);
        if (!q) return;
        const unit = String(r?.unit || '').trim() || 'units';
        qtyByUnit[unit] = (qtyByUnit[unit] || 0) + q;
    });
    if (Object.keys(qtyByUnit).length === 0 && num(totals.totalQty)) {
        qtyByUnit[String(totals.qtyUnit || '').trim() || 'units'] = num(totals.totalQty);
    }
    const qtyParts = Object.entries(qtyByUnit).map(([u, q]) => `${fmt(q)} ${u}`);

    // Per-phase productivity: today against the plan and against the crew's
    // own last week, plus where the phase stands job-to-date.
    const phaseHoursOf = (code) => crewRows.reduce((t, r) => t + num(r?.hours?.[code]), 0);
    const from7 = moment(reportDate).subtract(7, 'days').format('YYYY-MM-DD');

    const phaseLines = codes.map((code) => {
        const q = qtyRows.find((r) => String(r?.costCode || '').trim() === code) || {};
        const qty = num(q.installedToday);
        const hrs = phaseHoursOf(code);
        const unit = String(q.unit || '').trim();

        // Installed per day: this job's field reports plus HeavyJob postings.
        const dailyQty = {};
        (qtyHist.daily?.[code] || []).forEach((d) => { dailyQty[d.date] = (dailyQty[d.date] || 0) + d.qty; });
        Object.entries(fpQty[code] || {}).forEach(([d, v]) => { dailyQty[d] = (dailyQty[d] || 0) + v; });

        const inWeek = (d) => d >= from7 && d < reportDate;
        const weekQty = Object.entries(dailyQty).reduce((t, [d, v]) => t + (inWeek(d) ? v : 0), 0);
        const weekHrs = Object.entries(actualHours[code] || {}).reduce((t, [d, v]) => t + (inWeek(d) ? v : 0), 0);
        const pace = weekQty > 0 && weekHrs > 0 ? weekQty / weekHrs : 0;

        // Job-to-date, today folded in.
        const doneQty = Object.entries(dailyQty).reduce((t, [d, v]) => t + (d < reportDate ? v : 0), 0) + qty;
        const doneHrs = Object.entries(actualHours[code] || {}).reduce((t, [d, v]) => t + (d < reportDate ? v : 0), 0) + hrs;
        const toDateRate = doneQty > 0 && doneHrs > 0 ? doneQty / doneHrs : 0;

        // The plan: budget quantity over the phase's Burn Tool hours.
        const budgetQty = qtyHist.budgets[code] || 0;
        const budgetHrs = (phasedHours[code] || []).reduce((t, r) =>
            t + r.daily * (moment(r.end).diff(moment(r.start), 'days') + 1), 0);
        const planRate = budgetQty > 0 && budgetHrs > 0 ? budgetQty / budgetHrs : 0;

        const rate = qty > 0 && hrs > 0 ? qty / hrs : 0;
        // Hours today's quantity was worth at the plan's rate, against hours
        // actually spent. Positive = gain, negative = fade.
        const earned = planRate > 0 ? qty / planRate : 0;
        const gain = planRate > 0 && hrs > 0 ? earned - hrs : 0;
        const gainToDate = planRate > 0 && doneHrs > 0 ? doneQty / planRate - doneHrs : 0;

        return {
            code, desc: jobPhases[code] || '', qty, hrs, unit,
            rate, pace, planRate, toDateRate, budgetQty, budgetHrs,
            doneQty, doneHrs, earned, gain, gainToDate,
            status: phaseStatus(budgetQty, doneQty, intervals[code], reportDate, qtyHist.planFor(code)?.perDay),
        };
    });

    const worked = phaseLines.filter((p) => p.hrs > 0 || p.qty > 0);
    const measured = phaseLines.filter((p) => p.planRate > 0 && p.hrs > 0);
    const gainToday = measured.reduce((t, p) => t + p.gain, 0);
    const gainJtd = phaseLines.reduce((t, p) => t + p.gainToDate, 0);
    const jtdMeasured = phaseLines.some((p) => p.planRate > 0 && p.doneHrs > 0);

    // The rate tile reads off the phase that carried the most hours — one
    // blended number across mixed units would be meaningless.
    const lead = worked.reduce((m, p) => (p.hrs > (m?.hrs || 0) ? p : m), null);

    // Headline: the day in hours, then the productivity verdict.
    const headline = (() => {
        if (hours <= 0 && qtyParts.length === 0 && codes.length === 0) {
            return 'Nothing logged yet — hours and quantities go on the Progress tab.';
        }
        const bits = [];
        if (hours > 0) bits.push(`${fmt(hours)} ${hours === 1 ? 'hr' : 'hrs'}`);
        if (crewCount > 0) bits.push(`${crewCount} on the crew`);
        if (qtyParts.length) bits.push(qtyParts.join(' · '));
        const day = bits.join(' · ') || 'Nothing measurable yet';
        if (!measured.length) return `${day}.`;
        if (gainToday <= -0.5) return `${day} — ${fmt(Math.abs(gainToday))} hrs behind the plan today.`;
        if (gainToday >= 0.5) return `${day} — ${fmt(gainToday)} hrs to the good today.`;
        return `${day} — right on plan today.`;
    })();

    // Findings, worst fade first. Only productivity: what the hours earned,
    // how today compares to the crew's own week, and where the phase stands.
    const findings = [];

    [...measured].sort((a, b) => a.gain - b.gain).forEach((p) => {
        const label = `${p.code}${p.desc ? ` — ${p.desc}` : ''}`;
        const unit = p.unit || 'units';
        if (p.qty <= 0) {
            findings.push({
                tone: 'fade',
                text: `${label}: ${fmt(p.hrs)} hrs with nothing installed against them. That's ${fmt(p.hrs)} hrs of straight fade unless the quantity is still to be entered.`,
            });
        } else if (p.gain <= -0.5) {
            findings.push({
                tone: 'fade',
                text: `${label}: ${rateFmt(p.rate)} ${unit}/hr against a plan of ${rateFmt(p.planRate)}. Today ran ${fmt(Math.abs(p.gain))} hrs over what the quantity earned.`,
            });
        } else if (p.gain >= 0.5) {
            findings.push({
                tone: 'gain',
                text: `${label}: ${rateFmt(p.rate)} ${unit}/hr against a plan of ${rateFmt(p.planRate)} — ${fmt(p.gain)} hrs picked up.`,
            });
        }
    });

    // Against the crew's own last week — catches slippage even where the job
    // has no budget quantity to measure the plan from.
    worked.forEach((p) => {
        if (!(p.rate > 0 && p.pace > 0)) return;
        const pct = Math.round((p.rate / p.pace - 1) * 100);
        if (Math.abs(pct) < 15) return;
        const label = `${p.code}${p.desc ? ` — ${p.desc}` : ''}`;
        const unit = p.unit || 'units';
        findings.push({
            tone: pct < 0 ? 'fade' : 'gain',
            text: pct < 0
                ? `${label} ran ${Math.abs(pct)}% under its own 7-day pace — ${rateFmt(p.rate)} ${unit}/hr today vs ${rateFmt(p.pace)} last week.`
                : `${label} beat its 7-day pace by ${pct}% — ${rateFmt(p.rate)} ${unit}/hr today vs ${rateFmt(p.pace)} last week.`,
        });
    });

    // Where the phase sits overall — a good day inside a bad phase still needs
    // saying, and this is the number the PSR will carry.
    phaseLines.forEach((p) => {
        const label = `${p.code}${p.desc ? ` — ${p.desc}` : ''}`;
        const unit = p.unit || 'units';
        if (p.planRate > 0 && p.toDateRate > 0 && p.gainToDate <= -1) {
            findings.push({
                tone: 'fade',
                text: `${label} is ${fmt(Math.abs(p.gainToDate))} hrs under water job-to-date — running ${rateFmt(p.toDateRate)} ${unit}/hr against the ${rateFmt(p.planRate)} it was bid at.`,
            });
        }
        if (p.status?.state === 'behind') {
            findings.push({
                tone: 'fade',
                text: `${label} is behind its window — ${p.status.pctDone}% installed with ${p.status.pctPlanned}% of the schedule gone.`,
            });
        }
    });

    if (hours > 0 && codes.length === 0) {
        findings.push({
            tone: 'fade',
            text: `${fmt(hours)} hrs with no phase picked — none of it earns against a cost code.`,
        });
    }
    if (findings.length === 0 && hours > 0) {
        findings.push({
            tone: measured.length ? 'gain' : 'flat',
            text: measured.length
                ? 'Nothing slipping today — every phase came in at or above its plan.'
                : 'No budget quantity or phased hours on these phases yet, so today has nothing to measure against. Quantities on the Progress tab start the comparison.',
        });
    }

    return (
        <View>
            <View style={{ flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between', columnGap: 10 }}>
                <Text style={{ fontSize: 14, fontWeight: '700', color: '#0F172A' }}>Today's productivity</Text>
                <Text style={{ fontSize: 11, color: '#94A3B8' }}>
                    {reportDate ? moment(reportDate, 'YYYY-MM-DD').format('ddd, MMM D') : ''}
                </Text>
            </View>
            <Text style={{ fontSize: 13, color: '#334155', marginTop: 4, lineHeight: '18px' }}>{headline}</Text>

            {/* The day in numbers, ending on the two that matter */}
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', columnGap: 8, rowGap: 8, marginTop: 12 }}>
                <Tile
                    value={hours > 0 ? fmt(hours) : '—'}
                    label="Hours"
                    sub={crewCount > 0 ? `${crewCount} crew · ${fmt(hours / crewCount)} avg` : null}
                />
                <Tile
                    value={qtyParts.length ? qtyParts[0] : '—'}
                    label="Installed"
                    sub={qtyParts.length > 1 ? qtyParts.slice(1).join(' · ') : null}
                />
                <Tile
                    value={lead?.rate > 0 ? `${rateFmt(lead.rate)}` : '—'}
                    label={lead?.rate > 0 ? `${lead.unit || 'units'}/hr · ${lead.code}` : 'Rate'}
                    sub={lead?.planRate > 0
                        ? `plan ${rateFmt(lead.planRate)}`
                        : lead?.pace > 0 ? `last week ${rateFmt(lead.pace)}` : null}
                />
                <Tile
                    value={measured.length ? signed(gainToday) : '—'}
                    label="Gain / fade today"
                    color={gainToday >= 0 ? GAIN : FADE}
                    sub={measured.length ? 'earned vs worked' : 'no plan rate yet'}
                />
                <Tile
                    value={jtdMeasured ? signed(gainJtd) : '—'}
                    label="Job to date"
                    color={gainJtd >= 0 ? GAIN : FADE}
                    sub={jtdMeasured ? 'these phases' : null}
                />
            </View>

            {/* Phase by phase: what it cost, what it earned */}
            {worked.length > 0 && (
                <View style={{ marginTop: 12, borderRadius: 10, border: '1px solid #E2E8F0', overflow: 'hidden' }}>
                    <View style={{
                        flexDirection: 'row', alignItems: 'center', columnGap: 8,
                        backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0',
                        paddingTop: 6, paddingBottom: 6, paddingLeft: 10, paddingRight: 10,
                    }}>
                        <Text style={{ flex: 1, minWidth: 0, fontSize: 10, fontWeight: '700', color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5 }}>Phase</Text>
                        <Text style={{ width: 52, textAlign: 'right', fontSize: 10, fontWeight: '700', color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5 }}>Hrs</Text>
                        <Text style={{ width: 84, textAlign: 'right', fontSize: 10, fontWeight: '700', color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5 }}>Installed</Text>
                        <Text style={{ width: 104, textAlign: 'right', fontSize: 10, fontWeight: '700', color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5 }}>Rate vs plan</Text>
                        <Text style={{ width: 60, textAlign: 'right', fontSize: 10, fontWeight: '700', color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5 }}>+/−</Text>
                    </View>
                    {worked.map((p, i) => {
                        const target = p.planRate || p.pace;
                        const pct = p.rate > 0 && target > 0 ? Math.round((p.rate / target - 1) * 100) : null;
                        const rateColor = pct === null ? '#334155' : pct >= 5 ? GAIN : pct <= -5 ? FADE : FLAT;
                        return (
                            <View
                                key={p.code}
                                style={{
                                    flexDirection: 'row', alignItems: 'center', columnGap: 8,
                                    paddingTop: 8, paddingBottom: 8, paddingLeft: 10, paddingRight: 10,
                                    borderTop: i > 0 ? '1px solid #E2E8F0' : 'none',
                                    backgroundColor: i % 2 ? '#F8FAFC' : '#fff',
                                }}
                            >
                                <Text style={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: '600', color: '#0F172A' }}>
                                    {`${p.code}${p.desc ? ` — ${p.desc}` : ''}`}
                                </Text>
                                <Text style={{ width: 52, textAlign: 'right', fontSize: 12, color: '#334155', whiteSpace: 'nowrap' }}>
                                    {p.hrs > 0 ? fmt(p.hrs) : '—'}
                                </Text>
                                <Text style={{ width: 84, textAlign: 'right', fontSize: 12, color: '#334155', whiteSpace: 'nowrap' }}>
                                    {p.qty > 0 ? `${fmt(p.qty)}${p.unit ? ` ${p.unit}` : ''}` : '—'}
                                </Text>
                                <Text style={{ width: 104, textAlign: 'right', fontSize: 11, color: rateColor, whiteSpace: 'nowrap' }}>
                                    {p.rate > 0
                                        ? `${rateFmt(p.rate)}${target > 0 ? ` vs ${rateFmt(target)}` : ''}`
                                        : '—'}
                                </Text>
                                <Text style={{ width: 60, textAlign: 'right', fontSize: 12, fontWeight: '700', color: p.gain >= 0 ? GAIN : FADE, whiteSpace: 'nowrap' }}>
                                    {p.planRate > 0 && p.hrs > 0 ? signed(p.gain) : ''}
                                </Text>
                            </View>
                        );
                    })}
                </View>
            )}

            {findings.length > 0 && (
                <View style={{ marginTop: 14 }}>
                    <Text style={{ fontSize: 11, fontWeight: '700', color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Where the day went
                    </Text>
                    {findings.slice(0, 6).map((f, i) => <Finding key={i} tone={f.tone} text={f.text} />)}
                </View>
            )}
        </View>
    );
};

export default DayRecap;
